import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";
import { NAV_LINKS, waLink } from "@/lib/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled ? "border-b border-border bg-ivory/85 shadow-nav backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-20 sm:px-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo" aria-label="Lobang Home Inspection home">
            <svg viewBox="265 275 725 710" className="h-14 w-16 shrink-0 rounded-lg" aria-hidden="true"><image href="/assets/lobang-logo.jpg" width="1254" height="1254" /></svg>
            <span className="leading-none">
              <span className="block font-serif text-lg font-bold tracking-wide text-charcoal">LOBANG</span>
              <span className="block font-cinzel text-[9px] font-semibold uppercase tracking-[0.32em] text-gold-dark">
                Home Inspection
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-semibold tracking-wide transition-colors duration-300 hover:text-gold-dark ${
                    isActive ? "text-gold-dark" : "text-charcoal-soft"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-cta"
              className="hidden items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-[13px] font-semibold text-ivory transition-colors duration-300 hover:bg-gold-dark sm:inline-flex"
            >
              <MessageCircle className="h-4 w-4" />
              Book on WhatsApp
            </a>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white/70 text-charcoal lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-50 flex flex-col bg-ivory"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex h-16 items-center justify-between px-5">
              <span className="flex items-center gap-2.5">
                <svg viewBox="265 275 725 710" className="h-14 w-16 shrink-0 rounded-lg" aria-hidden="true"><image href="/assets/lobang-logo.jpg" width="1254" height="1254" /></svg>
                <span className="font-serif text-lg font-bold text-charcoal">LOBANG</span>
              </span>
              <button
                type="button"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-charcoal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block border-b border-border/70 py-4 font-serif text-3xl font-semibold transition-colors ${
                        isActive ? "text-gold-dark" : "text-charcoal"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="p-8">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-whatsapp-cta"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-wa px-6 py-4 text-sm font-bold text-white shadow-wa"
              >
                <MessageCircle className="h-5 w-5" />
                Book on WhatsApp — +65 8490 0316
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
